/**
 * Community comparison for the diagnostic results screen ("How You Compare").
 *
 * Pure derivations over already-fetched community_data docs — the Firestore
 * fetch stays app-side. Shared by web OnboardingTestReview and mobile
 * AssessmentReview so the dialog is byte-identical across platforms.
 *
 * Difficulty bands are deliberately the same 80/50 cuts as
 * study/communityStats.getDifficultyLevel — the diagnostic summary must never
 * disagree with the per-question community popups.
 */

/**
 * Sum per-question community aggregates into one summary.
 *
 * @param {Array<Object>} docs community_data docs ({ times_answered, times_answered_correct });
 *                             nullish docs/fields count as 0
 * @returns {{ totalAnswered: number, totalCorrect: number, communityPct: number|null,
 *             hasData: boolean, coveredCount: number }}
 *          communityPct is null (never NaN) when nothing has been answered;
 *          coveredCount is the number of questions with at least one answer
 *          (a doc that exists but was never answered is not covered)
 */
export function summarizeCommunityAnswers(docs) {
  const rows = Array.isArray(docs) ? docs : [];
  const totalAnswered = rows.reduce((sum, doc) => sum + (doc?.times_answered ?? 0), 0);
  const totalCorrect = rows.reduce((sum, doc) => sum + (doc?.times_answered_correct ?? 0), 0);
  const coveredCount = rows.filter((doc) => (doc?.times_answered ?? 0) > 0).length;
  const hasData = totalAnswered > 0;
  return {
    totalAnswered,
    totalCorrect,
    communityPct: hasData ? Math.round((100 * totalCorrect) / totalAnswered) : null,
    hasData,
    coveredCount,
  };
}

/**
 * Minimum sample before the "How You Compare" comparison is shown at all.
 * Below these thresholds the community average is either absent or too noisy
 * to be honest (a single prior answer reads "Community average: 100%"), and
 * the empty state ("no community data yet") signals an unused app — so the
 * apps hide the entry point entirely instead.
 */
export const COMMUNITY_COMPARISON_MIN_ANSWERS = 25;
export const COMMUNITY_COMPARISON_MIN_COVERAGE = 0.5;

/**
 * Should the diagnostic results screen offer the community comparison?
 * True only when the sample is big enough to be honest: at least
 * COMMUNITY_COMPARISON_MIN_ANSWERS total answers AND at least
 * COMMUNITY_COMPARISON_MIN_COVERAGE of the diagnostic's questions have been
 * answered by someone (a subset average isn't comparable to the user's
 * full-test score). Inclusive at both boundaries.
 *
 * @param {{ totalAnswered: number, coveredCount: number }|null|undefined} summary
 *        summarizeCommunityAnswers() output (nullish → false)
 * @param {number} totalQuestions number of questions in the diagnostic
 * @returns {boolean}
 */
export function hasSufficientCommunityData(summary, totalQuestions) {
  if (!summary || !(totalQuestions > 0)) return false;
  const answers = summary.totalAnswered ?? 0;
  const covered = summary.coveredCount ?? 0;
  return (
    answers >= COMMUNITY_COMPARISON_MIN_ANSWERS &&
    covered >= totalQuestions * COMMUNITY_COMPARISON_MIN_COVERAGE
  );
}

/**
 * Difficulty band for a community average. Tone is a semantic key for
 * @learnzapp/core/theme getToneColors, never a color.
 *
 * @param {number|null} communityPct rounded community average (null → null)
 * @returns {{ key: string, label: string, tone: string }|null}
 */
export function getCommunityDifficulty(communityPct) {
  if (communityPct === null || communityPct === undefined) return null;
  if (communityPct >= 80) return { key: 'easy', label: 'Easy', tone: 'success' };
  if (communityPct >= 50) return { key: 'moderate', label: 'Moderate', tone: 'warning' };
  return { key: 'challenging', label: 'Challenging', tone: 'error' };
}

/**
 * Full copy + row structure for the community comparison dialog.
 *
 * @param {Object} input
 * @param {number} input.userScorePct  the user's diagnostic score (0–100)
 * @param {number|null} input.communityPct rounded community average
 * @param {boolean} input.hasData      whether any community answers exist
 * @returns {{ title: string, subtitle: string,
 *             rows: Array<{ key: string, label: string, value: string }>,
 *             difficulty: ({ key: string, label: string, tone: string }|null),
 *             comparisonLine: string }}
 */
export function buildCommunityComparisonCopy({ userScorePct, communityPct, hasData } = {}) {
  const userPct = Math.round(userScorePct ?? 0);
  const rows = [{ key: 'user', label: 'Your score', value: `${userPct}%` }];

  if (!hasData || communityPct === null || communityPct === undefined) {
    return {
      title: 'How You Compare',
      subtitle: 'See how other learners performed on this assessment',
      rows,
      difficulty: null,
      comparisonLine: 'No community data yet for these questions — be one of the first!',
    };
  }

  const difficulty = getCommunityDifficulty(communityPct);
  rows.push({ key: 'community', label: 'Community average', value: `${communityPct}%` });
  rows.push({ key: 'difficulty', label: 'Difficulty', value: difficulty.label });

  const delta = userPct - communityPct;
  let comparisonLine;
  if (delta >= 5) {
    comparisonLine = `You scored ${delta} points above the community average — great work.`;
  } else if (delta <= -5) {
    comparisonLine = `You scored ${-delta} points below the community average — the full review shows where to catch up.`;
  } else {
    comparisonLine = "You're right in line with the community average.";
  }

  return {
    title: 'How You Compare',
    subtitle: 'See how other learners performed on this assessment',
    rows,
    difficulty,
    comparisonLine,
  };
}
